import { bbDeepCopy } from 'app/shared/utils/bbDeepCopy.util';
import { WidgetType } from '../enums/WidgetType';
import { IWidget } from '../interfaces/IWidget';
import { IDataWidget, IAnimationData } from '../redux/timeline-data.model';

class Widget implements IWidget {
	public isSelected: boolean;
	public isNew: boolean;
	public calculatedConfig: any = {};
	public fromAppId: string;
	public interfaceConfig: any = {};

	private _currentStateSet: string;
	private _currentState: string;
	private _data: IDataWidget;

	// for convenient quick access
	private _id: string;
	private _type: WidgetType;
	private _label: string;
	private _grouping: string[];
	private _animations: IAnimationData[];
	private _animatedConfig: any = {};

	// the config from the currently active stateSet and state  (future proof)
	private _config: any;


	constructor(data: IDataWidget, stateSet: string = 'default', state: string = 'default') {
		this.isSelected = false;
		if (data != null) {
			this._id = data.id;
			this.update(data, stateSet, state);
		}
	}

	get data(): IDataWidget {
		return this._data;
	}

	get id(): string {
		return this._id;
	}

	get label(): string {
		return this._label;
	}

	get type(): WidgetType {
		return this._type;
	}

	get config(): any {
		return this._config;
	}

	get grouping(): string[] {
		return this._grouping || [];
	}

	get animations(): any {
		return this._animations;
	}

	public update(data: IDataWidget, stateSet: string = 'default', state: string = 'default'): void {
		if (data != null) {
			this._data = data;
			this._label = data.label;
			this._grouping = data.grouping;
			this._animations = data.animations;
			this.fromAppId = data.fromAppId;
			this.setTypeByString(data.widgetdefinitiontype);
		}
		this.changeStateSet(stateSet);
		this.changeState(state);
		this.findAndSetConfig();
	}

	public getConfigValue(name: string): any {
		if (this._config && typeof this._config[name] !== 'undefined') {
			return this._config[name];
		} else {
			return null;
		}
	}

	public getAllConfigValues(name: string): any { // gets values from all statesets and states for 1 config value
		if (this._data && this._data.statesets && this._data.statesets.length > 0) {
			const data: any[] = new Array<any>();
			for (let i = 0, l: number = this._data.statesets.length; i < l; i++) {
				for (let j = 0, ll: number = this._data.statesets[i].states.length; j < ll; j++) {
					const config: any = this._data.statesets[i].states[j].config;
					if (typeof config[name] !== 'undefined') {
						data.push(config[name]);
					}
				}
			}
			return data;
		}
		return null;
	}

	// for in the future (probably)
	public getConfigByState(stateSet: string, state: string): void {
		if (this._data && this._data.statesets && this._data.statesets.length > 0) {
			for (let i = 0, l: number = this._data.statesets.length; i < l; i++) {
				if (this._data.statesets[i].name === stateSet) {
					for (let j = 0, ll: number = this._data.statesets[i].states.length; j < ll; j++) {
						if (this._data.statesets[i].states[j].name === state) {
							return bbDeepCopy(this._data.statesets[i].states[j].config);
						}
					}
				}
			}
		}
		return null;
	}

	// for in the future (probably)
	public changeStateSet(stateSet: string): void {
		this._currentStateSet = stateSet;
		this.findAndSetConfig();
	}

	// for in the future (probably)
	public changeState(state: string): void {
		this._currentState = state;
		this.findAndSetConfig();
	}

	public setAnimatedConfig(animatedConfig: any) {
		this._animatedConfig = animatedConfig;
		this.calculateConfig();
	}

	private findAndSetConfig(): void {
		// get config from the correct stateset and state
		if (this._data && this._data.statesets && this._data.statesets.length > 0) {
			for (let i = 0, l: number = this._data.statesets.length; i < l; i++) {
				if (this._data.statesets[i].name === this._currentStateSet) {
					for (let j = 0, ll: number = this._data.statesets[i].states.length; j < ll; j++) {
						if (this._data.statesets[i].states[j].name === this._currentState) {
							const conf = bbDeepCopy(this._data.statesets[i].states[j].config);
							if (this._currentState !== 'default') {
								const defaultConf = bbDeepCopy(this._data.statesets[i].states[0].config);
								const c = {...defaultConf, ...conf};
								if (c.textFormat?.textFormat) {
									c.textFormat.textFormat = {...defaultConf.textFormat.textFormat, ...(conf?.textFormat?.textFormat || {})};
								}
								this._config = c;
							} else {
								this._config = conf;
							}
						}
					}
				}
			}
		}
		this.calculateConfig();
	}

	private calculateConfig(): any {
		this.calculatedConfig = { ...this._config, ...this._animatedConfig };
	}

	private setTypeByString(type: string): void {
		switch (type) {
			case 'rectangle':
				this._type = WidgetType.Rectangle;
				break;
			case 'image':
				this._type = WidgetType.Image;
				break;
			case 'button':
				this._type = WidgetType.Button;
				break;
			case 'video':
				this._type = WidgetType.Video;
				break;
			case 'chapter':
				this._type = WidgetType.Chapter;
				break;
			case 'audio':
				this._type = WidgetType.Audio;
				break;
			case 'textLabel':
				this._type = WidgetType.TextLabel;
				break;
			case 'app':
				this._type = WidgetType.App;
				break;
			case 'formTextField':
				this._type = WidgetType.FormTextField;
				break;
			case 'formTextArea':
				this._type = WidgetType.FormTextArea;
				break;
			case 'formSelect':
				this._type = WidgetType.FormSelect;
				break;
			case 'timer':
				this._type = WidgetType.Timer;
				break;
			case 'custom':
				this._type = WidgetType.Custom;
				break;
			case 'action_only':
				this._type = WidgetType.ActionOnly;
				break;
		}
	}
}

export { Widget };
